import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Backdrop,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { MouseEvent, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAssetDestroyMutation, useAssetListQuery } from '../api/asset.js';
import { useSnapshotCreateMutation } from '../api/snapshot.js';

export function AssetView() {
  return <div>Asset</div>;
}

export function AssetsView() {
  const { projectId } = useParams();
  if (!projectId) throw new Error('Missing parameter');

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const certificateSnapshot = useSnapshotCreateMutation('certificate');
  const assetDestroy = useAssetDestroyMutation();
  const [confirmingAssetId, setConfirmingAssetId] = useState<string | null>(null);

  const handleRowClick = (assetId: string) => {
    navigate(`/projects/${projectId}/assets/${assetId}`);
  };

  const handleDeleteClick = (assetId: string) => {
    setConfirmingAssetId(assetId);
  };

  const handleCertificateSnapshot = (assetId: string, event: MouseEvent) => {
    event.stopPropagation();
    certificateSnapshot.mutate(
      { projectId, resourceId: assetId },
      {
        onSuccess: () => enqueueSnackbar('Certificate snapshot queued', { variant: 'success' }),
        onError: (err) =>
          enqueueSnackbar(`Failed to queue certificate snapshot: ${err.message}`, { variant: 'error' })
      }
    );
  };

  const creatingAssetId = certificateSnapshot.variables?.resourceId;
  const deletingAssetId = assetDestroy.variables?.assetId;
  const handleConfirmDelete = () => {
    if (!confirmingAssetId) return;
    assetDestroy.mutate(
      { projectId, assetId: confirmingAssetId },
      {
        onSuccess: () => enqueueSnackbar('Asset deleted', { variant: 'success' }),
        onError: (err) =>
          enqueueSnackbar(`Failed to delete asset: ${err instanceof Error ? err.message : String(err)}`, {
            variant: 'error'
          }),
        onSettled: () => setConfirmingAssetId(null)
      }
    );
  };

  const { data: assets, isLoading, isError } = useAssetListQuery(projectId);
  if (isLoading) {
    return (
      <Backdrop open={true}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (isError || !assets) {
    return <Typography variant="body1">Unable to load assets. Please try again later.</Typography>;
  }

  return (
    <Paper elevation={1} sx={{ padding: 2 }}>
      <Typography variant="h5">Assets</Typography>
      <TableContainer>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Fresh</TableCell>
              <TableCell>Hostname</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Port</TableCell>
              <TableCell>Timestamp</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assets.map((asset) => (
              <TableRow
                key={asset.id}
                hover
                onClick={() => handleRowClick(asset.id)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{asset.id}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={asset.status}
                    color={asset.status === 'active' ? 'success' : 'error'}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={asset.isFresh() ? 'fresh' : 'not fresh'}
                    color={asset.isFresh() ? 'success' : 'error'}
                  />
                </TableCell>

                <TableCell>{asset.hostname}</TableCell>
                <TableCell>{asset.address}</TableCell>
                <TableCell>{asset.port}</TableCell>
                <TableCell>{asset.updatedDate.toLocaleDateString()}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Snapshot" arrow>
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Snapshot"
                        disabled={certificateSnapshot.isLoading && creatingAssetId === asset.id}
                        onClick={(event) => handleCertificateSnapshot(asset.id, event)}
                      >
                        <CameraAltIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete" arrow>
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Delete"
                        color="error"
                        disabled={assetDestroy.isLoading && deletingAssetId === asset.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(asset.id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={!!confirmingAssetId} onClose={() => setConfirmingAssetId(null)}>
        <DialogTitle>Delete Asset</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete asset {confirmingAssetId}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmingAssetId(null)}>Cancel</Button>
          <Button color="error" onClick={handleConfirmDelete} disabled={assetDestroy.isLoading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
